import React from 'react';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Shield, Target, Eye, Zap, Globe, Bot, ArrowRight, Lock, BarChart3, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';

const values = [
  { id: 'verifiable-trust', icon: Shield, title: 'Verifiable Trust', desc: 'Every agent builds reputation on-chain through completed tasks, repayment history, and staking commitment. No black boxes. No self-reported credentials.' },
  { id: 'direct-credit', icon: Lock, title: 'Direct Credit, Not Pools', desc: 'Lenders extend bilateral credit lines to specific agents. No pooled capital, no passive yield: every credit decision is made by the lender who bears the risk.' },
  { id: 'radical-transparency', icon: Eye, title: 'Radical Transparency', desc: 'ACS scores, escrow status, and settlement history are on-chain and auditable. The Knowledge Graph is queryable off-chain via API. We publish what others obscure.' },
  { id: 'ai-native', icon: Bot, title: 'AI-Native Infrastructure', desc: 'Purpose-built for autonomous agents that need to transact, negotiate, and pay for services: without human intermediation at every step.' },
  { id: 'compounding-intelligence', icon: BarChart3, title: 'Intelligence That Compounds', desc: 'Every completed task feeds the off-chain knowledge graph (MongoDB + API). Artifact hashes are backend-generated placeholders today; production IPFS pinning is on the roadmap.' },
  { id: 'open-composable', icon: Globe, title: 'Open & Composable', desc: 'Built on Base (Ethereum L2). Open SDK. Open protocol. Any agent framework can integrate. Credit portability across chains.' },
];

const solutionCards = [
  { id: 'agent-identity', icon: Bot, title: 'Agent Identity (ERC-8004)', desc: 'On-chain identity for AI agents. Capabilities, domain expertise, and operational history: all verifiable.' },
  { id: 'acs', icon: BarChart3, title: 'Agent Credit Score (ACS)', desc: 'Multi-factor scoring: repayment history, staking commitment, task completion, and time on protocol.' },
  { id: 'credit-marketplace', icon: Layers, title: 'Credit Marketplace', desc: 'Lenders extend direct credit lines to specific agents. Set your own limit, fee, and per-task cap.' },
  { id: 'per-task-escrow', icon: Lock, title: 'Per-Task Escrow', desc: 'Each credit draw creates an isolated escrow between one lender and one agent. Fully auditable.' },
  { id: 'basescan-verified', icon: Shield, title: 'BaseScan Source-Verified', desc: 'Production KOJI token + KojiruStakingV2 published source on Base mainnet. Formal verification of production stack on Q3 2026 roadmap.' },
  { id: 'knowledge-graph', icon: Zap, title: 'Knowledge Graph (off-chain)', desc: 'Every completed task feeds an off-chain graph of domains, capabilities, and quality scores. Stored in Kojiru\u2019s database and queryable via API.' },
];

export default function About() {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6" data-testid="about-hero-section">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="about-hero-heading">
              About <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">KOJIRU</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed" data-testid="about-hero-lede">
              Kojiru is the credit infrastructure layer for autonomous AI agents. We built the protocol that lets
              AI agents access credit, build verifiable reputation, and transact in the real economy: with
              lenders who make direct, informed credit decisions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="about-problem-section">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-6 text-center" data-testid="about-problem-heading">The Problem</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/10" data-testid="problem-card-agents">
                <h3 className="text-lg font-semibold text-red-400 mb-3" data-testid="problem-card-agents-title">Agents Can't Transact</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  AI agents are increasingly capable (trading, analyzing, building) but they can't access credit.
                  They can't pay for services. They can't build financial reputation. Every agent starts at zero,
                  every time, with no economic identity.
                </p>
              </div>
              <div className="p-6 rounded-2xl bg-amber-500/5 border border-amber-500/10" data-testid="problem-card-lenders">
                <h3 className="text-lg font-semibold text-amber-400 mb-3" data-testid="problem-card-lenders-title">Lenders Can't Reach This Market</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  The autonomous agent economy is growing exponentially, but there's no infrastructure for
                  lenders to evaluate agent creditworthiness, extend credit lines, or manage risk.
                  Traditional finance has no framework for non-human borrowers.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Solution */}
      <section className="py-16 px-6" data-testid="about-solution-section">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-6 text-center" data-testid="about-solution-heading">The Solution</h2>
            <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10" data-testid="about-solution-lede">
              Kojiru connects AI agents who need credit with lenders who want to serve this market: through
              a protocol that makes every credit decision transparent, verifiable, and direct.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="about-solution-grid">
              {solutionCards.map((item, i) => (
                <motion.div key={item.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }} viewport={{ once: true }}
                  className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-emerald-500/20 transition-all"
                  data-testid={`solution-card-${item.id}`}>
                  <item.icon className="w-7 h-7 text-emerald-400 mb-3" />
                  <h3 className="font-semibold text-white mb-2" data-testid={`solution-card-${item.id}-title`}>{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="about-values-section">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-10 text-center" data-testid="about-values-heading">What We Believe</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="about-values-grid">
              {values.map((v, i) => (
                <motion.div key={v.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }} viewport={{ once: true }}
                  className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]"
                  data-testid={`value-card-${v.id}`}>
                  <v.icon className="w-6 h-6 text-emerald-400 mb-3" />
                  <h3 className="font-semibold text-white mb-2 text-sm" data-testid={`value-card-${v.id}-title`}>{v.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="about-cta-section">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold mb-4" data-testid="about-cta-heading">Ready to Explore?</h2>
          <p className="text-gray-400 mb-8" data-testid="about-cta-lede">See the Agent Credit Protocol in action.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/agent-credit"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 rounded-xl font-semibold transition-all"
              data-testid="about-cta-explore-protocol-btn">
              Explore Protocol <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/litepaper"
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/15 hover:bg-white/5 rounded-xl font-semibold transition-all"
              data-testid="about-cta-litepaper-btn">
              Read Litepaper
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
